import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogOut, Mail, Disc3, BadgeCheck, AlertTriangle, Music, RefreshCcw, Loader2, Pencil, ShieldAlert, Home as HomeIcon, User as UserIcon, BarChart3, Apple, X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import {
  APPLE_MUSIC_STOREFRONTS,
  DEFAULT_STOREFRONT,
  detectStorefrontFromLocale,
} from '@/lib/appleMusic';
import SpotifyConnectCard from '@/components/SpotifyConnectCard';
import InstallPwaProfileCard from '@/components/InstallPwaProfileCard';
import SubscriptionStatusCard from '@/components/SubscriptionStatusCard';
import Avatar from '@/components/Avatar';
import MarqueeBanner from '@/components/MarqueeBanner';
import VipManagerCard from '@/components/VipManagerCard';
import SongstatsUsageCard from '@/components/SongstatsUsageCard';
import PassQuotaCard from '@/components/PassQuotaCard';
import AppleMusicSyncCard from '@/components/AppleMusicSyncCard';
import { HelpButton } from '@/components/HowItWorks';
import { toast } from 'sonner';

export default function Profile() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [spotifyStatus, setSpotifyStatus] = useState(null);
  // Default-home toggle state. Falls back to 'crate' if the user record
  // doesn't have the field yet (pre-existing users created before this
  // feature shipped). Source of truth is user.default_home.
  const [savingHome, setSavingHome] = useState(false);
  const defaultHome = user?.default_home || 'crate';

  const handleDefaultHomeChange = async (value) => {
    if (value === defaultHome || savingHome) return;
    setSavingHome(true);
    try {
      await api.patch('/profile/me', { default_home: value });
      await refreshUser?.();
      toast.success(
        value === 'profile'
          ? 'Default home set to Profile'
          : 'Default home set to Collection'
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update default home');
    } finally {
      setSavingHome(false);
    }
  };

  // Apple Music storefront — controls the country used when building
  // "Open in Apple Music" deep links so tracks open in the user's
  // regional catalog. Backend clamps to a curated allowlist.
  const currentStorefront = user?.apple_music_storefront || DEFAULT_STOREFRONT;
  const [savingStorefront, setSavingStorefront] = useState(false);

  const handleStorefrontChange = async (value) => {
    if (!value || value === currentStorefront || savingStorefront) return;
    setSavingStorefront(true);
    try {
      await api.patch('/profile/me', { apple_music_storefront: value });
      await refreshUser?.();
      const label =
        APPLE_MUSIC_STOREFRONTS.find((s) => s.code === value)?.label || value.toUpperCase();
      toast.success(`Apple Music region set to ${label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update Apple Music region');
    } finally {
      setSavingStorefront(false);
    }
  };

  // Region auto-detect suggestion (Phase AM1.2).
  //
  // We only show the "Set to X?" banner when:
  //   * The user has NOT explicitly set a storefront yet
  //   * The detected locale storefront differs from the default ('us')
  //   * The user has not dismissed the suggestion previously
  //
  // Dismissal is stored in localStorage so it doesn't nag across page
  // loads. Explicitly choosing a region in the dropdown implicitly
  // dismisses the banner (we recheck user.apple_music_storefront).
  const suggestedStorefront = detectStorefrontFromLocale();
  const hasExplicitStorefront = !!user?.apple_music_storefront;
  const [suggestionDismissed, setSuggestionDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem('groovlens.appleMusicRegionSuggestionDismissed') === '1';
    } catch (_e) {
      return false;
    }
  });
  const showRegionSuggestion =
    !hasExplicitStorefront &&
    suggestedStorefront !== DEFAULT_STOREFRONT &&
    !suggestionDismissed;
  const suggestedLabel =
    APPLE_MUSIC_STOREFRONTS.find((s) => s.code === suggestedStorefront)?.label ||
    suggestedStorefront.toUpperCase();

  const dismissRegionSuggestion = () => {
    setSuggestionDismissed(true);
    try {
      window.localStorage.setItem('groovlens.appleMusicRegionSuggestionDismissed', '1');
    } catch (_e) {
      /* ignore quota / private-mode errors */
    }
  };

  const acceptRegionSuggestion = async () => {
    await handleStorefrontChange(suggestedStorefront);
    dismissRegionSuggestion();
  };

  const fetchSpotify = useCallback(async () => {
    try {
      const { data } = await api.get('/spotify/status');
      setSpotifyStatus(data);
    } catch (e) {
      // soft fail
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get('/stats/overview');
        if (active) setStats(data);
      } catch (e) {
        toast.error('Failed to load stats');
      } finally {
        if (active) setLoading(false);
      }
    })();
    fetchSpotify();
    return () => {
      active = false;
    };
  }, [fetchSpotify]);

  const handleSignout = () => {
    logout();
    toast.success('Signed out');
    navigate('/');
  };

  // Note: "Look up BPM & Key" and "Find music videos" used to live on
  // this page but were moved to the Collection (Crate) page where they
  // make more sense — those tools operate on the collection, not on
  // profile preferences.

  const [resettingSub, setResettingSub] = useState(false);
  const handleAdminResetSub = async () => {
    if (!window.confirm('Wipe your subscription record and start a fresh 7-day trial? This clears any stale Stripe IDs.')) return;
    setResettingSub(true);
    try {
      const { data } = await api.post('/admin/reset-my-subscription');
      toast.success(data?.message || 'Subscription reset.');
      // Trigger a page reload so /auth/me re-fetches the fresh subscription state
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally {
      setResettingSub(false);
    }
  };

  return (
    <div className="max-w-2xl">
      {/* Weekly stats ticker — auto-scrolls a compact set of "this week's
          numbers" so users get an at-a-glance vibe of their recent digging.
          Renders nothing (silently) when the user has fewer than 2 stats. */}
      <MarqueeBanner className="mb-3" />

      {/* Spotify Connect */}
      <div className="mt-1">
        <SpotifyConnectCard status={spotifyStatus} onChange={fetchSpotify} />
      </div>

      <div className="mt-5 rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center gap-4">
          <Avatar user={user} size={56} dataTestId="profile-avatar" />
          <div className="min-w-0 flex-1">
            <div className="font-display text-lg font-semibold truncate">
              {user?.username || user?.name || 'GroovLens listener'}
            </div>
            <div className="text-sm text-muted-foreground truncate inline-flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> {user?.email}
            </div>
            {user?.bio && (
              <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{user.bio}</div>
            )}
          </div>
          <Button
            asChild
            variant="outline"
            size="sm"
            data-testid="profile-edit-link"
            className="shrink-0"
          >
            <Link to="/profile/edit">
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
            </Link>
          </Button>
        </div>
        <Separator className="my-5" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <StatTile icon={Disc3} label="Total" value={loading ? '—' : stats?.total ?? 0} />
          <StatTile icon={Music} label="In collection" value={loading ? '—' : stats?.active ?? 0} />
          <StatTile icon={BadgeCheck} label="Owned" value={loading ? '—' : stats?.owned ?? 0} />
          <StatTile icon={AlertTriangle} label="To fix" value={loading ? '—' : stats?.unidentified ?? 0} />
        </div>
        {!loading && stats?.top_genres?.length > 0 && (
          <>
            <Separator className="my-5" />
            <div>
              <div className="text-xs uppercase font-mono text-muted-foreground mb-2">Top genres</div>
              <div className="flex flex-wrap gap-2">
                {stats.top_genres.map((g) => (
                  <span
                    key={g.genre}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-border bg-secondary text-xs"
                  >
                    {g.genre} <span className="text-muted-foreground font-mono">{g.count}</span>
                  </span>
                ))}
              </div>
            </div>
          </>
        )}
        {!loading && stats?.top_artists?.length > 0 && (
          <>
            <Separator className="my-5" />
            <div>
              <div className="text-xs uppercase font-mono text-muted-foreground mb-2">Top artists</div>
              <div className="flex flex-wrap gap-2">
                {stats.top_artists.map((a) => (
                  <span
                    key={a.artist}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-border bg-secondary text-xs"
                  >
                    {a.artist} <span className="text-muted-foreground font-mono">{a.count}</span>
                  </span>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="mt-4 space-y-3">
        {/* Default home toggle — lets the user choose whether logging in
            lands them on the Collection or Profile page. Persisted on the
            user record server-side so it follows across devices / PWA
            installs. Default is 'crate' (the value page). */}
        <div
          className="rounded-2xl border border-border bg-card p-4 sm:p-5"
          data-testid="default-home-card"
        >
          <div className="flex items-center gap-2 mb-1">
            <HomeIcon className="w-4 h-4 text-[hsl(var(--primary))]" />
            <h3 className="font-display font-semibold">Default home</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Where you land after signing in. Switch anytime.
          </p>
          <div
            role="radiogroup"
            aria-label="Default home page"
            className="grid grid-cols-2 gap-2"
          >
            <button
              type="button"
              role="radio"
              aria-checked={defaultHome === 'crate'}
              disabled={savingHome}
              onClick={() => handleDefaultHomeChange('crate')}
              data-testid="default-home-option-crate"
              className={`flex items-center gap-2 p-3 rounded-xl border text-left text-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] ${
                defaultHome === 'crate'
                  ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-foreground'
                  : 'border-border bg-background hover:border-[hsl(var(--primary))]/40 text-muted-foreground'
              }`}
            >
              <Disc3 className={`w-4 h-4 shrink-0 ${defaultHome === 'crate' ? 'text-[hsl(var(--primary))]' : ''}`} />
              <span className="font-display font-semibold">Collection</span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={defaultHome === 'profile'}
              disabled={savingHome}
              onClick={() => handleDefaultHomeChange('profile')}
              data-testid="default-home-option-profile"
              className={`flex items-center gap-2 p-3 rounded-xl border text-left text-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] ${
                defaultHome === 'profile'
                  ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-foreground'
                  : 'border-border bg-background hover:border-[hsl(var(--primary))]/40 text-muted-foreground'
              }`}
            >
              <UserIcon className={`w-4 h-4 shrink-0 ${defaultHome === 'profile' ? 'text-[hsl(var(--primary))]' : ''}`} />
              <span className="font-display font-semibold">Profile</span>
            </button>
          </div>
        </div>

        {/* Apple Music region — controls storefront used when opening
            "Open in Apple Music" links so users land in their local
            catalog. Server-clamped to a curated allowlist. */}
        <div
          className="rounded-2xl border border-border bg-card p-4 sm:p-5"
          data-testid="apple-music-region-card"
        >
          <div className="flex items-center gap-2 mb-1">
            <Apple className="w-4 h-4 text-[hsl(var(--primary))]" />
            <h3 className="font-display font-semibold">Apple Music region</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Where Apple Music links should open. Change this if the app opens the wrong country storefront.
          </p>
          {showRegionSuggestion && (
            <div
              className="mb-3 flex items-start gap-2 rounded-xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 p-2.5"
              data-testid="apple-music-region-suggestion"
            >
              <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-[hsl(var(--primary))]" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-foreground">
                  Looks like you might be in <span className="font-semibold">{suggestedLabel}</span>.
                  Want Apple Music links to open in that store?
                </p>
                <div className="mt-2 flex gap-2">
                  <Button
                    size="sm"
                    onClick={acceptRegionSuggestion}
                    disabled={savingStorefront}
                    className="h-7 px-2.5 text-xs rounded-lg"
                    data-testid="apple-music-region-suggestion-accept"
                  >
                    {savingStorefront ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Apple className="w-3 h-3 mr-1" />
                    )}
                    Use {suggestedStorefront.toUpperCase()}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={dismissRegionSuggestion}
                    className="h-7 px-2 text-xs rounded-lg text-muted-foreground"
                    data-testid="apple-music-region-suggestion-dismiss"
                  >
                    Not now
                  </Button>
                </div>
              </div>
              <button
                type="button"
                aria-label="Dismiss region suggestion"
                onClick={dismissRegionSuggestion}
                className="w-6 h-6 rounded-md grid place-items-center text-muted-foreground hover:text-foreground hover:bg-background/60 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
                data-testid="apple-music-region-suggestion-close"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <Select
            value={currentStorefront}
            onValueChange={handleStorefrontChange}
            disabled={savingStorefront}
          >
            <SelectTrigger
              className="w-full sm:w-64 rounded-xl"
              data-testid="apple-music-region-select"
              aria-label="Apple Music region"
            >
              <SelectValue placeholder="Select region" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {APPLE_MUSIC_STOREFRONTS.map((s) => (
                <SelectItem
                  key={s.code}
                  value={s.code}
                  data-testid={`apple-music-region-option-${s.code}`}
                >
                  <span className="inline-flex items-center gap-2">
                    <span className="text-[10px] font-mono uppercase text-muted-foreground w-6">
                      {s.code}
                    </span>
                    <span>{s.label}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <AppleMusicSyncCard />

        <SubscriptionStatusCard />
        <InstallPwaProfileCard />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Link to="/stats">
          <Button
            variant="secondary"
            className="rounded-xl"
            data-testid="profile-view-stats-button"
          >
            <BarChart3 className="w-4 h-4 mr-2" /> View stats
          </Button>
        </Link>
        <Button
          variant="secondary"
          onClick={handleSignout}
          className="rounded-xl"
          data-testid="profile-signout-button"
        >
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </Button>
      </div>

      {/* Admin-only: subscription reset (founder/dev tools) */}
      {user?.is_admin && (
        <div className="mt-6 rounded-xl border border-[hsl(var(--destructive))]/30 bg-[hsl(var(--destructive))]/5 p-3 sm:p-4">
          <div className="flex items-start gap-2.5">
            <ShieldAlert className="w-4 h-4 mt-0.5 text-[hsl(var(--destructive))] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">Admin tools</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Wipe your own subscription record (clears stale Stripe customer/subscription IDs left over from test mode) and start a fresh 7-day trial. Visible only to admin emails.
              </p>
              <div className="mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAdminResetSub}
                  disabled={resettingSub}
                  className="rounded-lg h-8 text-xs border-[hsl(var(--destructive))]/40 text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10 hover:text-[hsl(var(--destructive))]"
                  data-testid="admin-reset-subscription-button"
                >
                  {resettingSub ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Resetting…</>
                  ) : (
                    <><RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Reset my subscription</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Pass activity — daily quota + received count + request-higher-limit
          modal. Available to all users (not admin-gated) since single-tier
          Pro model gives everyone the feature. */}
      <PassQuotaCard />
      {/* Admin-only VIP Lifetime Access manager. Rendered inside the same
          `user?.is_admin` gate as Admin tools so it stays hidden from
          regular users. Self-service add / remove with signup-status
          badges. */}
      {user?.is_admin && <VipManagerCard />}
      {/* Admin-only Songstats budget monitor — surfaces cache health + monthly
          call count so the admin knows when to bump to a paid Songstats tier. */}
      {user?.is_admin && <SongstatsUsageCard />}
      {/* Floating "?" help button — opens the How GroovLens Works modal.
          Rendered on Profile too (alongside Crate) so the orientation
          surface is reachable from the second main hub. */}
      <HelpButton />
    </div>
  );
}

function StatTile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-border bg-background p-3 sm:p-4">
      <div className="text-[10px] uppercase font-mono text-muted-foreground inline-flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="font-display text-2xl font-semibold mt-0.5">{value}</div>
    </div>
  );
}
